import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Check,
  ChevronDown,
  CircleCheck,
  ClipboardList,
  User,
} from "lucide-react";
import { useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Logo } from "@/components/brand";
import { CredentialCard } from "@/components/credential-card";
import { NoticeDialog } from "@/components/notice-dialog";
import { ReasonNotice } from "@/components/password-fields";
import { Showcase } from "@/components/showcase";
import { Button } from "@/components/ui/button";
import { Field, Form, InputGroup, controlClass, controlHeight } from "@/components/ui/field";
import { api, errorMessage } from "@/services/api";
import type { TranslationKey } from "@/constants/i18n";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";
import { useTypedSearch } from "@/hooks/use-typed-search";

/**
 * Sign-in hands a refusal it cannot fix here — an expired password, a locked
 * account — with the id filled in and the reason fixed, as the legacy screen
 * does; the person only has to send it.
 *
 * The reason is the API's own enum value, so it is both the preset and the
 * key to the sentence explaining why they were sent here.
 */
const searchSchema = z.object({
  reason: z.string().optional(),
  // The router parses search values as JSON, so a numeric id arrives as a number.
  employee_id: z.union([z.string(), z.number()]).transform(String).optional(),
});

interface ReasonOption {
  value: string;
  label: string;
}

/** What each refusal sign-in forwards means, in the person's own terms. */
const PRESET_MESSAGE: Record<string, TranslationKey> = {
  password_expired: "reset.expiredSubtitle",
  account_locked: "reset.lockedSubtitle",
};

const schema = z.object({
  employee_id: z.string().trim().min(1),
  reason: z.string().min(1),
});

type RequestInput = z.infer<typeof schema>;

function ResetPasswordRequestPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const search = useTypedSearch(searchSchema);
  // Sent here by a refusal, rather than having chosen to come: the form is
  // already answered, so both fields are read-only and only Send is left.
  const forwarded = search.reason ? PRESET_MESSAGE[search.reason] : undefined;

  // Served unauthenticated, from the same enum the database uses, so the list
  // here cannot drift from what the API will accept.
  const reasons = useQuery({
    queryKey: ["password-reset-reasons"],
    queryFn: async ({ signal }) =>
      (await api.get<ReasonOption[]>("/auth/password-reset-request/reasons", { signal })).data,
    staleTime: Infinity,
  });

  const form = useForm<RequestInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee_id: forwarded ? (search.employee_id ?? "") : "",
      reason: forwarded ? (search.reason ?? "") : "",
    },
    mode: "onChange",
  });

  const mutation = useMutation({
    mutationFn: async (input: RequestInput) =>
      (await api.post<{ message: string }>("/auth/password-reset-request", input)).data,
  });

  const errors = form.formState.errors;
  const receipt = useRef<HTMLDialogElement>(null);

  return (
    <div className="grid flex-1 gap-8 px-5 pt-6 pb-24 sm:px-8 lg:grid-cols-[minmax(0,1fr)_min(571px,42%)] lg:items-stretch lg:gap-10 lg:px-8 lg:py-8">
      <main className="flex flex-col">
        <div className="flex flex-1 items-center py-10 lg:py-6">
          <div className="mx-auto w-full max-w-[32.5rem]">
            <Logo className="mb-10 w-[140px]" />

            <h1 className="text-title-1 text-heading">{t("reset.title")}</h1>
            <p className="text-fg-muted text-body mt-2 text-pretty">{t("reset.subtitle")}</p>

            <Form
              className="mt-8 grid gap-4"
              onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
              noValidate
            >
              {/* A failed send is the more urgent of the two; until then, what
                  sign-in refused is the reason this screen is on the person's
                  hands at all, so it says so where it cannot be missed. */}
              {mutation.isError ? (
                <p
                  role="alert"
                  className="bg-danger-soft text-danger text-callout flex items-start gap-2.5 rounded-xl px-3.5 py-3"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>{errorMessage(mutation.error, t("reset.errorGeneric"))}</span>
                </p>
              ) : forwarded ? (
                <ReasonNotice>{t(forwarded)}</ReasonNotice>
              ) : null}

              <Field
                label={t("login.employeeId")}
                htmlFor="employee_id"
                required
                error={errors.employee_id && t("login.errorEmployeeIdRequired")}
              >
                <InputGroup
                  id="employee_id"
                  icon={User}
                  fieldSize="lg"
                  placeholder={t("reset.employeeIdPlaceholder")}
                  autoComplete="username"
                  inputMode="numeric"
                  readOnly={Boolean(forwarded)}
                  spellCheck={false}
                  aria-invalid={Boolean(errors.employee_id)}
                  {...form.register("employee_id")}
                />
              </Field>

              {/* The way out sits where sign-in puts its own — under the last
                  field, right-aligned, in the same weight. */}
              <div className="grid gap-4">
                <Field
                  label={t("reset.reason")}
                  htmlFor="reason"
                  required
                  error={errors.reason && t("reset.errorReasonRequired")}
                >
                  <ReasonPicker
                    options={reasons.data ?? []}
                    value={form.watch("reason")}
                    placeholder={t("reset.reasonPlaceholder")}
                    disabled={reasons.isLoading || Boolean(forwarded)}
                    invalid={Boolean(errors.reason)}
                    onChange={(value) =>
                      form.setValue("reason", value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  />
                </Field>

                {/* Unreachable while the request is in flight: leaving now abandons
                    it half-sent, and an anchor has no `disabled` to say so. */}
                <Link
                  to="/login"
                  aria-disabled={mutation.isPending || undefined}
                  tabIndex={mutation.isPending ? -1 : undefined}
                  className={cn(
                    "text-accent text-subhead justify-self-end font-medium underline-offset-4 hover:underline",
                    mutation.isPending && "pointer-events-none opacity-50",
                  )}
                >
                  {t("reset.back")}
                </Link>
              </div>

              <Button
                type="submit"
                size="xl"
                className="mt-3 w-full"
                disabled={!form.formState.isValid}
                loading={mutation.isPending}
                loadingLabel={t("reset.submitting")}
              >
                {t("reset.submit")}
              </Button>
            </Form>
          </div>
        </div>
      </main>

      {/* Decoration earns its place beside the form, not stacked under it on a
          phone, where it only pushes the footer out of reach. */}
      <div className="hidden lg:block">
        <Showcase visual={<CredentialCard />} />
      </div>

      {/* The API says what happened — a password on its way by email, or a
          request waiting on a manager — so the words come from there. */}
      {mutation.isSuccess ? (
        <NoticeDialog
          dialogRef={receipt}
          icon={CircleCheck}
          tone="success"
          title={t("reset.sentTitle")}
          description={mutation.data.message}
          onClose={() => mutation.reset()}
          actions={<Button onClick={() => void navigate("/login")}>{t("reset.back")}</Button>}
        />
      ) : null}
    </div>
  );
}

/**
 * The reason list, drawn as the same field it sits in: an icon, the chosen
 * label, a chevron — and a panel underneath that unfurls from its top edge.
 *
 * A native top-layer popover, so Esc and click-away close it without a listener
 * of our own. The rows are plain buttons reached by Tab: a `listbox` role would
 * promise arrow-key navigation this does not implement.
 */
function ReasonPicker({
  options,
  value,
  placeholder,
  disabled,
  invalid,
  onChange,
}: {
  options: ReasonOption[];
  value: string;
  placeholder: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const place = () => {
    const anchor = button.current?.getBoundingClientRect();
    const el = panel.current;
    if (!anchor || !el) return;
    Object.assign(el.style, {
      top: `${anchor.bottom + 6}px`,
      left: `${anchor.left}px`,
      width: `${anchor.width}px`,
    });
  };
  const current = options.find((option) => option.value === value);
  return (
    <>
      <button
        ref={button}
        id="reason"
        type="button"
        disabled={disabled}
        aria-invalid={invalid}
        aria-expanded={open}
        popoverTarget={id}
        className={cn(
          controlClass,
          controlHeight.lg,
          "flex items-center gap-2.5 pl-3 text-left",
          open && "border-accent ring-[3px] ring-[var(--accent-ring)]",
        )}
      >
        <ClipboardList className="text-fg-subtle size-4 shrink-0" aria-hidden />
        <span className={cn("min-w-0 flex-1 truncate", !current && "text-fg-subtle")}>
          {current?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            "text-fg-subtle size-4 shrink-0 transition-transform duration-[var(--duration-fast)]",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      <div
        ref={panel}
        id={id}
        popover="auto"
        onToggle={(event) => {
          setOpen(event.newState === "open");
          if (event.newState === "open") place();
        }}
        className="bg-surface-raised border-line rounded-card animate-popover fixed m-0 border p-1.5 shadow-[var(--shadow-float)]"
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              panel.current?.hidePopover();
              onChange(option.value);
            }}
            className={cn(
              "text-fg text-callout hover:bg-surface-inset flex w-full items-center gap-2.5",
              "rounded-field px-3 py-2 text-left transition-colors duration-[var(--duration-fast)]",
              option.value === value && "text-accent font-medium",
            )}
          >
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
            {/* A tick on the one in force, as a pop-up menu marks its choice. */}
            {option.value === value ? <Check className="size-4 shrink-0" aria-hidden /> : null}
          </button>
        ))}
      </div>
    </>
  );
}

export default ResetPasswordRequestPage;
